// FooterWrapper.tsx
import * as React from 'react'
import { flushSync } from 'react-dom'
import { GridRowSelectionModel } from '@mui/x-data-grid-premium'
import { TasksGridFooterProps } from 'types'

type WrapperInjected = {
  rowSelectionModel: GridRowSelectionModel
  setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
}

// Keep a single source of truth for injected keys so it doesn’t drift.
type InjectedKeys =
  | 'rowSelectionModel'
  | 'hasSubtaskSelected'
  | 'hasParentSelected'
  | 'onClosePopover'
  | 'onCloseMassDeletePopover'
  | 'onMassDelete'

type FooterOwnProps = Omit<TasksGridFooterProps, InjectedKeys> & {
  onMassDelete?: (
    _rowSelectionModel: GridRowSelectionModel,
    _setRowSelectionModel: React.Dispatch<React.SetStateAction<GridRowSelectionModel>>
  ) => void
  onClosePopover?: () => void
  onCloseMassDeletePopover?: () => void
}

type TaskFooterWrapperProps = WrapperInjected & {
  Component: React.ComponentType<TasksGridFooterProps>
  footerProps: FooterOwnProps
}
const TaskFooterWrapper: React.FC<TaskFooterWrapperProps> = ({
  rowSelectionModel,
  setRowSelectionModel,
  Component,
  footerProps,
}) => {
  const hasSubtaskSelected = React.useMemo(
    () => Array.from(rowSelectionModel?.ids ?? []).some(id => String(id).split('::')[1]?.startsWith('SUBTASK_')),
    [rowSelectionModel]
  )
  const hasParentSelected = React.useMemo(
    () => Array.from(rowSelectionModel?.ids ?? []).some(id => String(id).split('::')[1]?.startsWith('TASK_')),
    [rowSelectionModel]
  )

  const clearSelectionNow = React.useCallback(() => {
    flushSync(() => {
      setRowSelectionModel({ ids: new Set<string>(), type: 'include' })
      ;(document.activeElement as HTMLElement | null)?.blur?.()
    })
  }, [setRowSelectionModel])

  const handleClosePopover = React.useCallback(() => {
    clearSelectionNow()
    footerProps.onClosePopover?.()
  }, [clearSelectionNow, footerProps])

  const handleCloseMassDeletePopover = React.useCallback(() => {
    clearSelectionNow()
    footerProps.onCloseMassDeletePopover?.()
  }, [clearSelectionNow, footerProps])

  // Adapt the page’s onMassDelete(model, setter) to the footer’s onMassDelete()
  const handleMassDeleteBound = React.useCallback(() => {
    footerProps.onMassDelete?.(rowSelectionModel, setRowSelectionModel)
  }, [footerProps, rowSelectionModel, setRowSelectionModel])

  return (
    <Component
      {...footerProps}
      rowSelectionModel={rowSelectionModel}
      hasSubtaskSelected={hasSubtaskSelected}
      hasParentSelected={hasParentSelected}
      onClosePopover={handleClosePopover}
      onCloseMassDeletePopover={handleCloseMassDeletePopover}
      onMassDelete={handleMassDeleteBound}
    />
  )
}

export default React.memo(TaskFooterWrapper)
